import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Invoice } from '../types';
import { contractInvoicePaymentService } from '../services/api/contract-invoice-payment.service';
import './InvoiceDetailsStripePaymentsTab.css';
import Modal from '../components/Modal';

const logger = console;

interface PaymentTransaction {
  id: number;
  transaction_number: string;
  provider: string;
  provider_transaction_id: string;
  status: string;
  amount: number;
  currency: string;
  payment_method: string;
  initiated_at: string;
  completed_at: string | null;
  error_message: string | null;
}

interface PaymentIntentDetails {
  payment_intent_id: string;
  status: string;
  transaction_status: string;
  payment_status: string;
  invoice_payment_status: string;
  amount: number;
  currency: string;
  created: number;
  last_updated: number | null;
  error_message: string | null;
}

interface StripePaymentStatus {
  id: number;
  invoice_id: string;
  stripe_payment_intent_id: string;
  stripe_charge_id: string | null;
  payment_intent_status: string;
  charge_status: string | null;
  invoice_payment_status: string;
  event_type: string;
  event_reason: string | null;
  amount: number | null;
  currency: string | null;
  refunded_amount: number;
  is_current: boolean;
  allows_new_payment_intent: boolean;
  created_at: string | null;
}

interface InvoiceDetailsStripePaymentsTabProps {
  invoice: Invoice;
  onRefreshInvoice: () => void;
  refreshKey?: number;
}

const InvoiceDetailsStripePaymentsTab: React.FC<InvoiceDetailsStripePaymentsTabProps> = ({ invoice, onRefreshInvoice, refreshKey }) => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [stripePaymentStatuses, setStripePaymentStatuses] = useState<StripePaymentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentIntentStatus, setPaymentIntentStatus] = useState<string | null>(null);
  const [showPaymentIntentModal, setShowPaymentIntentModal] = useState(false);
  const [selectedPaymentIntent, setSelectedPaymentIntent] = useState<PaymentIntentDetails | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [allowsNewPaymentIntent, setAllowsNewPaymentIntent] = useState<boolean>(true);
  const [paymentIntentEvents, setPaymentIntentEvents] = useState<StripePaymentStatus[]>([]);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [selectedPaymentIntentId, setSelectedPaymentIntentId] = useState<string | null>(null);

  const fetchPaymentTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch payment transactions for this invoice
      try {
        const response = await apiService.get(`/api/v1/payment-transactions/invoice/${invoice.invoice_id}`);
        
        // Type assertion for the response
        const typedResponse = response as {
          invoice_id: string;
          invoice_name: string;
          transactions: Array<{
            id: number;
            transaction_number: string;
            provider: string;
            provider_transaction_id: string;
            status: string;
            amount: number;
            currency: string;
            payment_method: string;
            initiated_at: string;
            completed_at: string | null;
            error_message: string | null;
          }>;
          total_transactions: number;
        };
        
        // Filter for Stripe transactions only
        const stripeTransactions = typedResponse.transactions.filter((t) => 
          t.provider === 'STRIPE' || t.provider === 'stripe'
        );
        
        setTransactions(stripeTransactions);
      } catch (transErr: any) {
        logger.warn('Payment transactions endpoint not available');
        setTransactions([]);
      }
      
      // Fetch Stripe payment status history
      try {
        const statusResponse = await apiService.get(`/api/v1/stripe-billing/invoices/${invoice.invoice_id}/stripe-payment-status`);
        const statusData = statusResponse as StripePaymentStatus[];
        setStripePaymentStatuses(statusData);
        
        // Update allowsNewPaymentIntent state based on the current status
        if (statusData.length > 0) {
          const currentStatus = statusData.find(s => s.is_current);
          if (currentStatus) {
            setAllowsNewPaymentIntent(currentStatus.allows_new_payment_intent);
          } else {
            // If no current status, check the most recent one
            setAllowsNewPaymentIntent(statusData[0].allows_new_payment_intent);
          }
        } else {
          // No status records, so we can create a new payment intent
          setAllowsNewPaymentIntent(true);
        }
      } catch (statusErr: any) {
        logger.warn('Stripe payment status endpoint not available');
        setStripePaymentStatuses([]);
        // Default to allowing new payment intent if we can't check
        setAllowsNewPaymentIntent(true);
      }
      
    } catch (err: any) {
      console.error('Error fetching payment data:', err);
      setError(err.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPaymentTransactions();
  }, [invoice.invoice_id, refreshKey]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // First, refresh payment intent status from Stripe
      if (transactions.length > 0) {
        await apiService.post(`/api/v1/payment-intents/${invoice.invoice_id}/refresh`, {});
        logger.info(`Payment intent status refreshed for invoice ${invoice.invoice_id}`);
      }
      
      // Then refresh the transactions list
      await fetchPaymentTransactions();
    } catch (error) {
      console.error('Error refreshing payment intent status:', error);
      // Continue with fetching transactions even if refresh fails
      await fetchPaymentTransactions();
    }
  };
  
  const fetchPaymentIntentDetails = async (paymentIntentId: string) => {
    setModalLoading(true);
    setModalError(null);
    try {
      const result = await contractInvoicePaymentService.getPaymentIntentStatus(invoice.invoice_id);
      
      if (result.success) {
        setSelectedPaymentIntent({
          payment_intent_id: result.payment_intent_id || paymentIntentId,
          status: result.status,
          transaction_status: result.transaction_status,
          payment_status: result.payment_status,
          invoice_payment_status: result.invoice_payment_status,
          amount: result.amount,
          currency: result.currency,
          created: result.created,
          last_updated: result.last_updated,
          error_message: result.error_message
        });
        setShowPaymentIntentModal(true);
      } else {
        setModalError(result.error || 'Failed to fetch payment intent details');
      }
    } catch (error: any) {
      console.error('Error fetching payment intent details:', error);
      setModalError(error.message || 'Failed to fetch payment intent details');
    } finally {
      setModalLoading(false);
    }
  };

  const fetchPaymentIntentEvents = async (paymentIntentId: string) => {
    setModalLoading(true);
    setModalError(null);
    try {
      const events = await contractInvoicePaymentService.getPaymentIntentEvents(paymentIntentId);
      setPaymentIntentEvents(events);
      setSelectedPaymentIntentId(paymentIntentId);
      setShowEventsModal(true);
    } catch (error: any) {
      console.error('Error fetching payment intent events:', error);
      setModalError(error.message || 'Failed to fetch payment intent events');
      setShowEventsModal(true);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCreatePaymentIntent = async () => {
    try {
      if (!invoice) return;
      const response = await apiService.createStripePaymentIntent(invoice.invoice_id);
      
      // Only set status to 'created' if we have a payment_intent_id
      if (response?.payment_intent_id) {
        setPaymentIntentStatus('created');
      } else {
        setError('Payment intent creation failed - no payment intent ID received');
      }
      onRefreshInvoice();
      // Also refresh the payment transactions to show the new payment
      await fetchPaymentTransactions();
    } catch (error: any) {
      console.error('Error creating payment intent:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create payment intent.';
      setError(errorMessage);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    const classes: Record<string, string> = {
      created: 'badge-secondary',
      pending: 'badge-secondary',
      processing: 'badge-info',
      completed: 'badge-success',
      succeeded: 'badge-success',
      paid: 'badge-success',
      failed: 'badge-danger',
      payment_failed: 'badge-danger',
      canceled: 'badge-warning',
      cancelled: 'badge-warning',
      expired: 'badge-dark',
      refunded: 'badge-primary',
      disputed: 'badge-warning',
      written_off: 'badge-dark',
      requires_action: 'badge-info',
      requires_payment_method: 'badge-secondary',
      requires_confirmation: 'badge-secondary',
      not_due: 'badge-secondary'
    };
    return classes[statusLower] || 'badge-secondary';
  };

  const mapTransactionToInvoiceStatus = (transactionStatus: string): string => {
    const statusLower = transactionStatus.toLowerCase();
    
    // Map transaction statuses to invoice payment statuses
    const mapping: Record<string, string> = {
      created: 'pending',
      pending: 'pending',
      processing: 'processing',
      completed: 'paid',
      succeeded: 'paid',
      failed: 'failed',
      canceled: 'cancelled',
      cancelled: 'cancelled',
      expired: 'failed',
      refunded: 'refunded',
      disputed: 'disputed',
      written_off: 'written_off'
    };
    
    return mapping[statusLower] || 'pending';
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading && !refreshing) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="spinner"></div>
        <p style={{ marginTop: '10px', color: '#7f8c8d' }}>Loading payment transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#e74c3c' }}>
        <p><strong>Error:</strong> {error}</p>
        <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div className="stripe-payments-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Stripe Payment Transactions</h3>
        <button 
          className="btn btn-outline-primary btn-sm" 
          onClick={handleRefresh} 
          disabled={refreshing}
          title="Refresh payment intent status from Stripe and update transactions"
        >
          {refreshing ? 'Refreshing...' : '🔄 Refresh Status'}
        </button>
      </div>
      
      {(transactions.length > 0 || stripePaymentStatuses.length > 0) && (
        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Invoice Payment Status:</strong> 
              <span className={`badge ${getStatusBadgeClass(invoice.payment_status)}`} style={{ marginLeft: '8px' }}>
                {invoice.payment_status}
              </span>
            </div>
            <div>
              <strong>Stripe Payments:</strong> {stripePaymentStatuses.length} status entries, {transactions.length} transactions
              {transactions.filter(t => t.status === 'completed').length > 0 && (
                <span className="badge badge-success" style={{ marginLeft: '8px' }}>
                  ✓ {transactions.filter(t => t.status === 'completed').length} completed
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {transactions.length === 0 && stripePaymentStatuses.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
          <p>No Stripe payment transactions or payment status history found for this invoice.</p>
          {invoice.payment_status !== 'paid' && (
            <p style={{ marginTop: '10px', fontSize: '14px' }}>
              This invoice has not been paid via Stripe yet.
            </p>
          )}
          {invoice.payment_status !== 'paid' && allowsNewPaymentIntent && (
            <button
              className="btn btn-success"
              onClick={handleCreatePaymentIntent}
              style={{ marginTop: '20px' }}
              title="Create Stripe Payment Intent"
            >
              💳 Create Payment Intent
            </button>
          )}
          {invoice.payment_status !== 'paid' && !allowsNewPaymentIntent && (
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', textAlign: 'center' }}>
              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                ⚠ A payment intent is already in progress. Please wait for it to complete or be refunded before creating a new one.
              </p>
            </div>
          )}
          {paymentIntentStatus === 'created' && (
            <div style={{ marginTop: '20px', color: '#27ae60', fontWeight: 'bold' }}>
              ✓ Payment intent created successfully!
            </div>
          )}
        </div>
      )}
      
      {/* Stripe Payment Status History */}
      {stripePaymentStatuses.length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '12px' }}>Stripe Payment Status History</h4>
          <table className="table">
            <thead>
              <tr>
                <th>PI ID</th>
                <th>Charge ID</th>
                <th>Status</th>
                <th>Invoice Status</th>
                <th>Event</th>
                <th>Amount</th>
                <th>Refunded</th>
                <th>New Allowed</th>
                <th>Current</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {stripePaymentStatuses.map((status) => (
                <tr key={status.id} style={{ backgroundColor: status.is_current ? '#f0f8ff' : 'transparent' }}>
                  <td 
                    style={{ fontFamily: 'monospace', fontSize: '12px', cursor: 'pointer', color: '#3498db' }}
                    onClick={() => fetchPaymentIntentEvents(status.stripe_payment_intent_id)}
                    title="Click to view all events for this payment intent"
                  >
                    {status.stripe_payment_intent_id} 🔍
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{status.stripe_charge_id || 'N/A'}</td>
                  <td><span className={`badge ${getStatusBadgeClass(status.payment_intent_status)}`}>{status.payment_intent_status}</span></td>
                  <td>{status.charge_status && <span className={`badge ${getStatusBadgeClass(status.charge_status)}`}>{status.charge_status}</span>}</td>
                  <td><span className={`badge ${getStatusBadgeClass(status.invoice_payment_status)}`}>{status.invoice_payment_status}</span></td>
                  <td><span className="badge badge-secondary">{status.event_type}</span>{status.event_reason && <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>Reason: {status.event_reason}</div>}</td>
                  <td>{status.amount !== null && status.currency && formatCurrency(status.amount, status.currency)}</td>
                  <td>{status.refunded_amount > 0 && <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{formatCurrency(status.refunded_amount, status.currency || 'USD')}</span>}</td>
                  <td>
                    {status.allows_new_payment_intent ? (
                      <span className="badge badge-success">✓ Yes</span>
                    ) : (
                      <span className="badge badge-danger">✗ No</span>
                    )}
                  </td>
                  <td>{status.is_current && <span className="badge badge-success">✓ Current</span>}</td>
                  <td>{formatDate(status.created_at || '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Regular Transactions Table */}
      {transactions.length > 0 && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Transaction #</th>
                <th>Payment ID</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Initiated</th>
                <th>Completed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td>{transaction.transaction_number}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {transaction.provider_transaction_id}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span 
                        className={`badge ${getStatusBadgeClass(transaction.status)}`}
                        title={`Transaction Status: ${transaction.status}${transaction.error_message ? '\nError: ' + transaction.error_message : ''}`}
                      >
                        {transaction.status === 'completed' ? '✓ Completed' : transaction.status}
                      </span>
                      {mapTransactionToInvoiceStatus(transaction.status) !== invoice.payment_status && (
                        <span 
                          className="badge badge-secondary"
                          style={{ fontSize: '10px', padding: '2px 6px' }}
                          title="Invoice Payment Status"
                        >
                          Invoice: {invoice.payment_status}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{formatCurrency(transaction.amount, transaction.currency)}</td>
                  <td>{transaction.payment_method}</td>
                  <td>{formatDate(transaction.initiated_at)}</td>
                  <td>{formatDate(transaction.completed_at || '')}</td>
                  <td>
                    {transaction.status === 'completed' && (
                      <button 
                        className="btn btn-success btn-sm" 
                        title="Payment completed successfully"
                        disabled
                      >
                        ✓ Paid
                      </button>
                    )}
                    {transaction.status === 'failed' && transaction.error_message && (
                      <button 
                        className="btn btn-danger btn-sm" 
                        title={transaction.error_message}
                        onClick={() => alert(`Error: ${transaction.error_message}`)}
                      >
                        ⚠ Error
                      </button>
                    )}
                    <button
                      className="btn btn-info btn-sm"
                      style={{ marginLeft: '5px' }}
                      title="View payment intent details"
                      onClick={() => fetchPaymentIntentDetails(transaction.provider_transaction_id)}
                    >
                      🔍 Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPaymentIntentModal && selectedPaymentIntent && (
      <Modal
        title={`Payment Intent: ${selectedPaymentIntent.payment_intent_id}`}
        isOpen={true}
        onClose={() => setShowPaymentIntentModal(false)}
        size="lg"
      >
        {modalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '10px', color: '#7f8c8d' }}>Loading payment intent details...</p>
          </div>
        ) : modalError ? (
          <div style={{ padding: '20px', color: '#e74c3c' }}>
            <p><strong>Error:</strong> {modalError}</p>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowPaymentIntentModal(false)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {/* Payment Intent Info */}
              <div className="card">
                <div className="card-header">
                  <h4>Payment Intent Information</h4>
                </div>
                <div className="card-body">
                  <DetailRow label="Payment Intent ID" value={selectedPaymentIntent.payment_intent_id} />
                  <DetailRow label="Status" value={selectedPaymentIntent.status} />
                  <DetailRow label="Amount" value={formatCurrency(selectedPaymentIntent.amount / 100, selectedPaymentIntent.currency)} />
                  <DetailRow label="Currency" value={selectedPaymentIntent.currency.toUpperCase()} />
                  <DetailRow label="Created" value={new Date(selectedPaymentIntent.created * 1000).toLocaleString()} />
                  {selectedPaymentIntent.last_updated && (
                    <DetailRow label="Last Updated" value={new Date(selectedPaymentIntent.last_updated * 1000).toLocaleString()} />
                  )}
                </div>
              </div>
              
              {/* Status Information */}
              <div className="card">
                <div className="card-header">
                  <h4>Status Information</h4>
                </div>
                <div className="card-body">
                  <DetailRow 
                    label="Transaction Status" 
                    value={
                      <span className={`badge ${getStatusBadgeClass(selectedPaymentIntent.transaction_status)}`}>
                        {selectedPaymentIntent.transaction_status}
                      </span>
                    }
                  />
                  <DetailRow 
                    label="Payment Status" 
                    value={
                      <span className={`badge ${getStatusBadgeClass(selectedPaymentIntent.payment_status)}`}>
                        {selectedPaymentIntent.payment_status}
                      </span>
                    }
                  />
                  <DetailRow 
                    label="Invoice Payment Status" 
                    value={
                      <span className={`badge ${getStatusBadgeClass(selectedPaymentIntent.invoice_payment_status)}`}>
                        {selectedPaymentIntent.invoice_payment_status}
                      </span>
                    }
                  />
                </div>
              </div>
              
              {/* Error Information */}
              {selectedPaymentIntent.error_message && (
                <div className="card">
                  <div className="card-header">
                    <h4>Error Information</h4>
                  </div>
                  <div className="card-body">
                    <DetailRow label="Error" value={selectedPaymentIntent.error_message} />
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPaymentIntentModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowPaymentIntentModal(false);
                  handleRefresh();
                }}
              >
                Refresh Status
              </button>
            </div>
          </div>
        )}
      </Modal>
      )}

      {/* Payment Intent Events Modal */}
      {showEventsModal && (
      <Modal
        title={`Stripe Events for Payment Intent: ${selectedPaymentIntentId}`}
        isOpen={true}
        onClose={() => setShowEventsModal(false)}
        size="xl"
      >
        {modalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '10px', color: '#7f8c8d' }}>Loading payment intent events...</p>
          </div>
        ) : modalError ? (
          <div style={{ padding: '20px', color: '#e74c3c' }}>
            <p><strong>Error:</strong> {modalError}</p>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowEventsModal(false)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <p>
                <strong>Payment Intent ID:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: '#f4f4f4', padding: '2px 6px', borderRadius: '3px' }}>{selectedPaymentIntentId}</code>
              </p>
              <p>
                <strong>Total Events:</strong> {paymentIntentEvents.length}
              </p>
            </div>

            {paymentIntentEvents.length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event Type</th>
                      <th>PI Status</th>
                      <th>Charge Status</th>
                      <th>Invoice Status</th>
                      <th>Amount</th>
                      <th>Refunded</th>
                      <th>New Allowed</th>
                      <th>Current</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentIntentEvents.map((event, index) => (
                      <tr key={event.id} style={{ backgroundColor: event.is_current ? '#f0f8ff' : 'transparent' }}>
                        <td>{index + 1}</td>
                        <td><span className="badge badge-secondary">{event.event_type}</span></td>
                        <td><span className={`badge ${getStatusBadgeClass(event.payment_intent_status)}`}>{event.payment_intent_status}</span></td>
                        <td>{event.charge_status && <span className={`badge ${getStatusBadgeClass(event.charge_status)}`}>{event.charge_status}</span>}</td>
                        <td><span className={`badge ${getStatusBadgeClass(event.invoice_payment_status)}`}>{event.invoice_payment_status}</span></td>
                        <td>{event.amount !== null && event.currency && formatCurrency(event.amount, event.currency)}</td>
                        <td>{event.refunded_amount > 0 && <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{formatCurrency(event.refunded_amount, event.currency || 'USD')}</span>}</td>
                        <td>
                          {event.allows_new_payment_intent ? (
                            <span className="badge badge-success">✓ Yes</span>
                          ) : (
                            <span className="badge badge-danger">✗ No</span>
                          )}
                        </td>
                        <td>{event.is_current && <span className="badge badge-success">✓ Current</span>}</td>
                        <td>{formatDate(event.created_at || '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                <p>No events found for this payment intent.</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEventsModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowEventsModal(false);
                  handleRefresh();
                }}
              >
                Refresh All Status
              </button>
            </div>
          </div>
        )}
      </Modal>
      )}
    </div>
  );
};
  
export default InvoiceDetailsStripePaymentsTab;

/* -------------------------------------------------------------------------- */
/*                               Helper Component                             */
/* -------------------------------------------------------------------------- */
interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  isTotal?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isTotal = false }) => (
  <div className={`detail-row ${isTotal ? 'total' : ''}`}>
    <span className="detail-label read-only">{label}:</span>
    <span className="detail-value read-only">{value}</span>
  </div>
);
